import {
  ExploreFilters,
  ExploreSearchContextType,
  ExploreType,
  Weekday,
} from "@/types/explore"

export const EXPLORE_TYPE_ALL = "all"
export const EXPLORE_TYPE_HOTEL = "hotel"
export const EXPLORE_TYPE_RESTAURANT = "restaurant"
export const EXPLORE_TYPE_ATTRACTION = "attraction"
export const EXPLORE_TYPE_LOCATION = "location"
export const EXPLORE_TYPE_EVENT = "event"
export const EXPLORE_TYPE_GUIDE = "guide"

export const EXPLORE_PAGE_CONTEXT = "explore-page-context"
export const EXPLORE_COLLECTION_SEARCH_CONTEXT = "collection-context"
export const EXPLORE_INSPIRATION_SEARCH_CONTEXT = "inspiration-context"
export const EXPLORE_TRIP_SEARCH_CONTEXT = "trip-context"

// Place types that are controlled by backend's suggested_tab_order
export const EXPLORE_PLACE_TYPES: ExploreType[] = [
  EXPLORE_TYPE_LOCATION,
  EXPLORE_TYPE_HOTEL,
  EXPLORE_TYPE_RESTAURANT,
  EXPLORE_TYPE_ATTRACTION,
  EXPLORE_TYPE_EVENT,
]

// Non-place types that are always shown regardless of backend suggestion
export const EXPLORE_NON_PLACE_TYPES: ExploreType[] = [EXPLORE_TYPE_GUIDE]

// All explore types: computed from place types and non-place types
export const EXPLORE_TYPES: ExploreType[] = [
  EXPLORE_TYPE_ALL,
  ...EXPLORE_PLACE_TYPES,
  ...EXPLORE_NON_PLACE_TYPES,
]

export const EXPLORE_SEARCH_CONTEXT: ExploreSearchContextType[] = [
  EXPLORE_PAGE_CONTEXT,
  EXPLORE_COLLECTION_SEARCH_CONTEXT,
  EXPLORE_INSPIRATION_SEARCH_CONTEXT,
  EXPLORE_TRIP_SEARCH_CONTEXT,
]

export const EXPLORE_MAP_ID = "explore-map"
export const EXPLORE_MAP_DEBOUNCE_DELAY = 500
export const EXPLORE_MAP_PEEK_HEIGHT = 0.3

export { RECENT_SEARCHES_STORAGE_KEY as EXPLORE_RECENT_SEARCHES_STORAGE_KEY } from "./search"

export const MIN_USER_RATING_OPTIONS = [3, 3.5, 4, 4.5]

export const HOTEL_CLASS_OPTIONS = [
  { value: "two_star", label: "2-star" },
  { value: "three_star", label: "3-star" },
  { value: "four_star", label: "4-star" },
  { value: "five_star", label: "5-star" },
]

export const DISTANCE_OPTIONS_IMPERIAL = [
  { value: 1.6, label: "1 mile" },
  { value: 4.8, label: "3 miles" },
  { value: 16.1, label: "10 miles" },
  { value: 48.3, label: "30 miles" },
]

export const DISTANCE_OPTIONS_METRIC = [
  { value: 1.6, label: "1.5 km" },
  { value: 5, label: "5 km" },
  { value: 15, label: "15 km" },
  { value: 50, label: "50 km" },
]

// Budget filter values stay "$"-based tokens (used as filter state); only the
// displayed label is localized to the user's preferred currency symbol.
export const getBudgetOptions = (dollarSign = "$") => {
  return ["$$$$", "$$$", "$$", "$"].map((value) => ({
    value,
    label: dollarSign.repeat(value.length),
  }))
}

export const HOTEL_TYPE_OPTIONS = [
  { value: "hotel", label: "Hotel" },
  { value: "hostel", label: "Hostel" },
  { value: "campground", label: "Campground" },
  { value: "short_term_rental", label: "Vacation rental" },
  { value: "resort", label: "Resort" },
  { value: "motel", label: "Motel" },
]

export const CLASSLESS_HOTEL_TYPES = [
  "hostel",
  "campground",
  "short_term_rental",
]

export const HOTEL_VIBE_OPTIONS = [
  { value: "boutique", label: "Boutique" },
  { value: "luxury", label: "Luxury" },
  { value: "budget", label: "Budget" },
  { value: "business", label: "Business" },
  { value: "modern", label: "Modern" },
  { value: "historic", label: "Historic" },
  { value: "contemporary", label: "Contemporary" },
  { value: "traditional", label: "Traditional" },
  { value: "extended_stay", label: "Extended stay" },
  { value: "eco_friendly", label: "Eco-friendly" },
  { value: "heritage", label: "Heritage" },
  { value: "retreat", label: "Retreat" },
  { value: "themed", label: "Themed" },
]

export const HOTEL_FEATURES_OPTIONS: {
  value: keyof ExploreFilters
  label: string
}[] = [
  { value: "is_highly_reviewed", label: "Highly reviewed" },
  { value: "easy_parking", label: "Easy parking" },
  { value: "include_chains", label: "Exclude chains" },
]

export const ATTRACTION_VIBE_OPTIONS = [
  { value: "outdoor_activity", label: "Outdoor activity" },
  { value: "cultural_and_historical", label: "Cultural & Historical" },
  { value: "nightlife", label: "Nightlife" },
  { value: "cultural_show", label: "Cultural show" },
  { value: "guided_tour", label: "Guided tour" },
  { value: "relaxation", label: "Relaxation" },
  { value: "adventure_unique_sport", label: "Adventure" },
]

export const ATTRACTION_FEATURES_OPTIONS: {
  value: keyof ExploreFilters
  label: string
}[] = [
  { value: "is_highly_reviewed", label: "Highly reviewed" },
  { value: "is_new", label: "New" },
  { value: "is_touristy", label: "Touristy" },
  { value: "is_must_see", label: "Must see" },
  { value: "is_scenic", label: "Scenic" },
  { value: "is_family_friendly", label: "Family friendly" },
  { value: "easy_parking", label: "Easy parking" },
]

export const RESTAURANT_CUISINE_OPTIONS = [
  { value: "american", label: "American" },
  { value: "bakery", label: "Bakery" },
  { value: "cafe", label: "Cafe" },
  { value: "chinese", label: "Chinese" },
  { value: "french", label: "French" },
  { value: "indian", label: "Indian" },
  { value: "italian", label: "Italian" },
  { value: "japanese", label: "Japanese" },
  { value: "korean", label: "Korean" },
  { value: "mediterranean", label: "Mediterranean" },
  { value: "mexican", label: "Mexican" },
  { value: "pizza", label: "Pizza" },
  { value: "seafood", label: "Seafood" },
  { value: "sushi", label: "Sushi" },
  { value: "thai", label: "Thai" },
  { value: "vietnamese", label: "Vietnamese" },
]

export const WEEKDAY_OPTIONS: { value: Weekday; label: string }[] = [
  { value: "monday", label: "Monday" },
  { value: "tuesday", label: "Tuesday" },
  { value: "wednesday", label: "Wednesday" },
  { value: "thursday", label: "Thursday" },
  { value: "friday", label: "Friday" },
  { value: "saturday", label: "Saturday" },
  { value: "sunday", label: "Sunday" },
]

export const RESTAURANT_FEATURES_OPTIONS: {
  value: keyof ExploreFilters
  label: string
}[] = [
  { value: "is_looking_for_quiet", label: "Quiet atmosphere" },
  { value: "is_highly_reviewed", label: "Highly reviewed" },
  { value: "is_new", label: "New" },
  { value: "is_upscale", label: "Upscale" },
  { value: "is_casual", label: "Casual" },
  { value: "is_family_friendly", label: "Family friendly" },
  { value: "is_pet_friendly", label: "Pet friendly" },
  { value: "fine_dining", label: "Fine dining" },
  { value: "special_occasion", label: "Special occasion" },
  { value: "is_authentic", label: "Authentic" },
  { value: "serves_alcohol", label: "Serves alcohol" },
  { value: "easy_parking", label: "Easy parking" },
  { value: "include_chains", label: "Include chains" },
]
